import React, { useState, useEffect } from 'react';
import { Loader, SuccessModal, getIcon } from '@ohif/ui';
import { useTheme } from "../../ui/src/contextProviders";
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '@custom/store/store';
import { Form, Formik } from 'formik';
import getPrice from '@custom/rtk-apis/price/getPrice';
import { customStyle } from '@custom/Utils/utils';
import editInvestigation from '@custom/rtk-apis/billing/editInvestigation';
import addInvestigation from '@custom/rtk-apis/billing/addInvestigation';
import getInvestigationApi from '@custom/rtk-apis/billing/getInvestigation';

interface ModalityOption {
  label: string;
  value: string;
}

interface Props {
  setShowAddForm: (value: boolean) => void;
  investigationData?: any;
  onDataUpdated?: () => void;
}

const AddEditInvestigation = (props: Props) => {
  const dispatch = useDispatch<AppDispatch>();
  const { result: pricingData, loading: pricingLoader } = useSelector((state: RootState) => state.getPriceSliceResponse);
  const { isDarkMode } = useTheme();
  const hospitalId = sessionStorage.getItem('hospitalId') || localStorage.getItem('hospitalId');

  const [modalityOptions, setModalityOptions] = useState<ModalityOption[]>([]);
  const [success, setSuccess] = useState(false);
  const [msg, setMsg] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const investigationData = props.investigationData || {};
  const isEditMode = !!investigationData && Object.keys(investigationData).length > 0;

  useEffect(() => {
    dispatch(getPrice({ modality: '', studyRegion: '' }));
  }, [dispatch]);

  useEffect(() => {
    if (Array.isArray(pricingData)) {
      const uniqueModalities = Array.from(new Set(pricingData.map(item => item.modality)))
        .filter(Boolean)
        .map(modality => ({
          label: modality,
          value: modality,
        }));
      setModalityOptions(uniqueModalities);
    }
  }, [pricingData]);

  const initialValues = {
    servicesInvestigation: investigationData["Package Name"] || '',
    servicesGroup: investigationData["Services Included"] || '',
    price: investigationData["Price"]
    ? parseFloat(investigationData["Price"].replace(/[^0-9.-]+/g, '')) || ''
    : '',
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);

    if (isEditMode) {
      const updatedPayload = {
        hospitalId: hospitalId,
        uniqueCode: investigationData.uniqueCode,
        updatedPackageData: {
          "Package Name": values.servicesInvestigation,
          "Services Included": values.servicesGroup,
          "Price": parseFloat(values.price) || 0,
        },
      };

      try {
        const response = await dispatch(editInvestigation(updatedPayload));
        if (response.meta.requestStatus === 'fulfilled') {
          setMsg('Investigation updated successfully');
          setSuccess(true);
          await Promise.all([
            dispatch(getPrice({ modality: '', studyRegion: '' })),
            dispatch(getInvestigationApi()),
          ]);
          props.onDataUpdated?.();
          setTimeout(() => {
            setSuccess(false);
            props.setShowAddForm(false);
          }, 500);
        } else {
          setMsg('Failed to update investigation');
          setSuccess(true);
        }
      } catch (error) {
        console.error("Error updating investigation:", error);
        setMsg('An error occurred while updating');
        setSuccess(true);
      }
    } else {
      const newInvestigationPayload = {
        body: {
          hospitalId: hospitalId,
          packages: [
            {
              "Package Name": values.servicesInvestigation,
              "Services Included": values.servicesGroup,
              "Price": parseFloat(values.price) || 0,
            },
          ],
        },
      };

      try {
        const response = await dispatch(addInvestigation(newInvestigationPayload));
        if (response.meta.requestStatus === 'fulfilled') {
          setMsg('Investigation added successfully');
          setSuccess(true);
          await Promise.all([
            dispatch(getPrice({ modality: '', studyRegion: '' })),
            dispatch(getInvestigationApi()),
          ]);
          props.onDataUpdated?.();
          setTimeout(() => {
            setSuccess(false);
            props.setShowAddForm(false);
          }, 500);
        } else {
          setMsg('Failed to add investigation');
          setSuccess(true);
        }
      } catch (error) {
        console.error("Error adding investigation:", error);
        setMsg('An error occurred while adding');
        setSuccess(true);
      }
    }

    setSubmitting(false);
  };

  return (
    <div className='page page__fullBody' style={isDarkMode ? { background: '#090c29' } : customStyle.mainBackground}>
      <div className='page__body'>
        <div className='details'>
          <div className='form form--innersec'>
            <div className="mb-3">
              <button
                className=''
                onClick={() => props.setShowAddForm(false)}
                style={{ display: 'flex', alignItems: 'center' }}
              >
                {getIcon('icon-toolbar-back')}
                Back
              </button>
            </div>
            <section className='form__section'>
              <h1 className='form__section__title'>{isEditMode ? 'Edit Investigation' : 'Add Investigation'}</h1>
              <Formik
                initialValues={initialValues}
                onSubmit={handleSubmit}
                enableReinitialize={true}
              >
                {({
                  values,
                  handleChange,
                  handleBlur,
                  setFieldValue,
                  touched,
                  isSubmitting,
                }) => {
                  const isFormValid =
                    values.servicesInvestigation.trim() !== "" &&
                    values.servicesGroup.trim() !== "" &&
                    values.price > 0;

                  return (
                    <Form>
                      <div className='formRow formRow__3'>
                        <div className='formCol'>
                          <div className='form__Field'>
                            <label className="form__label" htmlFor='servicesInvestigation'>Services Investigation<span className="required-asterisk">*</span></label>
                            <input
                              type="text"
                              className='form__input form__input--sm'
                              id="servicesInvestigation"
                              name="servicesInvestigation"
                              placeholder="Services Investigation"
                              value={values.servicesInvestigation}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              autoComplete='off'
                            />
                          </div>
                        </div>
                        <div className='formCol'>
                          <div className='form__Field'>
                            <label className="form__label" htmlFor='servicesGroup'>Services Group<span className="required-asterisk">*</span></label>
                            <input
                              type="text"
                              className='form__input form__input--sm'
                              id="servicesGroup"
                              name="servicesGroup"
                              placeholder="Services Group"
                              value={values.servicesGroup}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              autoComplete='off'
                            />
                          </div>
                        </div>
                        <div className='formCol'>
                          <div className='form__Field'>
                            <label className="form__label" htmlFor='price'>Price<span className="required-asterisk">*</span></label>
                            <input
                              type="number"
                              className='form__input form__input--sm'
                              id="price"
                              name="price"
                              placeholder="Price"
                              min="1"
                              onChange={(e) => {
                                const price = parseFloat(e.target.value);
                                setFieldValue("price", price > 0 ? price : "");
                              }}
                              onBlur={handleBlur}
                              value={values.price}
                              autoComplete='off'
                            />
                            {values.price <= 0 && touched.price && (
                              <div style={{ color: "red", fontSize: "11.8px" }}>
                                Price must be greater than 0
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className='btn__group'>
                        <button className='btn btn__white' type="button" onClick={() => props.setShowAddForm(false)}>
                          Cancel
                        </button>
                        <button className='btn btn__primary' type='submit' disabled={!isFormValid}>
                          {isEditMode ? 'Update' : 'Save'}
                        </button>
                      </div>
                    </Form>
                  )
                }}
              </Formik>
            </section>
          </div>
        </div>
        <SuccessModal
          isSuccessModalOpen={success}
          title={msg}
          handleCloseModal={() => setSuccess(false)}
        />
        <Loader isLoaderOpen={pricingLoader || submitting} />
      </div>
    </div>
  );
};

export default AddEditInvestigation;
